import React, { useState } from "react";
import PropTypes from "prop-types";

import Money from "../../../components/Money";
import { formatDate } from "../../../utilities/common";
import { PlayerTile } from "./PlayerTile";
import {
    GROUPS,
    filterByGroup,
    groupCounts,
    totalOnTable,
    totalOwedPayout,
    emptyText,
} from "./playerGroups";

import "./Ledger.mobile.css";

/**
 * Mobile-native presentation of the live single-game ledger. Same data
 * and palette as the desktop table — restructured into a phone-first
 * layout: game-info header, summary band, All / Owing / Cashed-out
 * tabs, full-width player tiles, and a persistent bottom action dock.
 *
 * Pure presentation: all mutations are delegated to the callbacks so
 * the same component drops into both the real ledger and the prototype.
 */
export function LedgerMobile({
    game,
    onBack,
    onAddPlayer,
    onBuyIn,
    onCashOut,
    onSettle,
}) {
    const [tab, setTab] = useState("all");
    const [query, setQuery] = useState("");
    const [expandedId, setExpandedId] = useState(null);

    const players = game.players ?? [];
    const counts = groupCounts(players);
    const onTable = totalOnTable(players);
    const owedPayout = totalOwedPayout(players);

    const grouped = filterByGroup(players, tab);
    const q = query.trim().toLowerCase();
    const visible = q
        ? grouped.filter((p) => p.name?.toLowerCase().includes(q))
        : grouped;

    return (
        <div className="mlg">
            {/* Pinned chrome — only the roster below scrolls. */}
            {onBack && (
                <div className="mlg-topbar">
                    <button
                        type="button"
                        className="mlg-back"
                        onClick={onBack}
                    >
                        <span aria-hidden="true">‹</span> Games
                    </button>
                </div>
            )}

            <GameHeaderMobile game={game} playerCount={players.length} />

            <GameSummary
                onTable={onTable}
                owedPayout={owedPayout}
                buyIns={game.totalBuyIns ?? 0}
                net={game.totalNet ?? 0}
                playerCount={players.length}
            />

            <RosterTabs tab={tab} counts={counts} onSelect={setTab} />

            <PlayerSearch value={query} onChange={setQuery} />

            <div className="mlg-roster">
                {visible.length === 0 ? (
                    <div className="mlg-empty">
                        {emptyText(tab, players.length, query.trim())}
                    </div>
                ) : (
                    visible.map((p) => (
                        <PlayerTile
                            key={p.id}
                            gameId={game.id}
                            player={p}
                            expanded={expandedId === p.id}
                            onToggle={() =>
                                setExpandedId((cur) =>
                                    cur === p.id ? null : p.id,
                                )
                            }
                            onBuyIn={() => onBuyIn?.(p)}
                            onCashOut={() => onCashOut?.(p)}
                        />
                    ))
                )}
            </div>

            <LedgerDock onAddPlayer={onAddPlayer} onSettle={onSettle} />
        </div>
    );
}

function GameHeaderMobile({ game, playerCount }) {
    const meta = formatDate(game.date);
    return (
        <header className="mlg-head">
            <h1 className="mlg-title">{renderTitle(game.description)}</h1>
            <div className="mlg-head-row">
                <div className="mlg-status">
                    <span className="mlg-live-dot" aria-hidden="true" />
                    Live · {playerCount} player{playerCount === 1 ? "" : "s"}
                </div>
                {meta && <div className="mlg-head-meta">{meta}</div>}
            </div>
        </header>
    );
}

function GameSummary({ onTable, owedPayout, buyIns, net, playerCount }) {
    const netClass = net > 0 ? "is-up" : net < 0 ? "is-down" : "is-zero";

    // Unsettled buy-ins (money owed TO the table) take priority. Once those
    // clear, surface what the house still owes OUT as payouts — styled
    // distinctly so it doesn't read like an unsettled buy-in.
    let lead;
    if (onTable > 0) {
        lead = { state: "owed", label: "On the table · unsettled", value: <Money amount={onTable} /> };
    } else if (owedPayout > 0) {
        lead = { state: "payout", label: "Owed to players", value: <Money amount={owedPayout} /> };
    } else {
        lead = { state: "settled", label: "On the table", value: "All settled" };
    }

    return (
        <div className="mlg-summary">
            <div className={`mlg-summary-lead is-${lead.state}`}>
                <div className="mlg-summary-l">{lead.label}</div>
                <div className={`mlg-summary-v ${lead.state}`}>{lead.value}</div>
            </div>
            <dl className="mlg-summary-stats">
                <div>
                    <dt>Buy-ins</dt>
                    <dd>
                        <Money amount={buyIns} />
                    </dd>
                </div>
                <div>
                    <dt>Net</dt>
                    <dd className={netClass}>
                        <Money amount={net} signed={net !== 0} />
                    </dd>
                </div>
                <div>
                    <dt>Players</dt>
                    <dd>{playerCount}</dd>
                </div>
            </dl>
        </div>
    );
}

function RosterTabs({ tab, counts, onSelect }) {
    return (
        <div className="mlg-tabs" role="tablist">
            {GROUPS.map((g) => (
                <button
                    key={g.key}
                    type="button"
                    role="tab"
                    aria-selected={tab === g.key}
                    className={`mlg-tab${tab === g.key ? " is-on" : ""}`}
                    onClick={() => onSelect(g.key)}
                >
                    {g.label}
                    <span className="mlg-tab-count">{counts[g.key]}</span>
                </button>
            ))}
        </div>
    );
}

function PlayerSearch({ value, onChange }) {
    return (
        <div className="mlg-search">
            <span className="mlg-search-icon" aria-hidden="true">⌕</span>
            <input
                type="search"
                className="mlg-search-input"
                placeholder="Filter players"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                aria-label="Filter players by name"
            />
            {value && (
                <button
                    type="button"
                    className="mlg-search-clear"
                    aria-label="Clear filter"
                    onClick={() => onChange("")}
                >
                    ×
                </button>
            )}
        </div>
    );
}

function LedgerDock({ onAddPlayer, onSettle }) {
    return (
        <div className="mlg-dock">
            <button type="button" className="mlg-btn primary" onClick={onAddPlayer}>
                + Add player
            </button>
            {onSettle && (
                <button type="button" className="mlg-btn ghost" onClick={onSettle}>
                    Settle up
                    <span className="mlg-pro">Pro</span>
                </button>
            )}
        </div>
    );
}

// Mirror the desktop mast: italicize the last word so it picks up the accent.
function renderTitle(description) {
    if (!description) return <em>Untitled</em>;
    const trimmed = description.trim();
    const idx = trimmed.lastIndexOf(" ");
    if (idx === -1) return trimmed;
    return (
        <>
            {trimmed.slice(0, idx)} <em>{trimmed.slice(idx + 1)}</em>
        </>
    );
}


LedgerMobile.propTypes = {
    game: PropTypes.object.isRequired,
    onBack: PropTypes.func,
    onAddPlayer: PropTypes.func,
    onBuyIn: PropTypes.func,
    onCashOut: PropTypes.func,
    onSettle: PropTypes.func,
};
